import { useState } from 'react';
import { parseDueShorthand } from '../../lib/todos';
import { formatDueLabel } from '../../lib/todos';
import { todayISO } from '../../lib/dates';

const DUE_CHOICES = [
  { key: 'today', label: 'Today', days: 0 },
  { key: 'tomorrow', label: 'Tomorrow', days: 1 },
  { key: 'week', label: 'Next week', days: 7 },
  { key: 'none', label: 'No date', days: null },
];

function shift(today, days) {
  if (days === null) return null;
  const [y, m, d] = today.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/* Always visible at the top of the list.
 *
 * Adding a task was a modal, which is a whole page change to write one line.
 * The full form still exists for description, priority and links — this is
 * for the nine times out of ten when a title and a date is the whole task.
 */
export default function QuickAdd({ profiles, defaultAssignee, onAdd }) {
  const today = todayISO();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('today');
  const [assignee, setAssignee] = useState(defaultAssignee || '');
  const [saving, setSaving] = useState(false);

  // Shown live, so it's obvious the trailing word is being read as a date
  // before you commit to it.
  const parsed = parseDueShorthand(title, today);
  const chosen = DUE_CHOICES.find((c) => c.key === due);
  const dueDate = parsed.dueDate || shift(today, chosen?.days ?? null);

  const submit = async (e) => {
    e.preventDefault();
    if (!parsed.title.trim() || saving) return;
    setSaving(true);
    try {
      await onAdd({
        title: parsed.title.trim(),
        due_date: dueDate,
        assigned_to: assignee || null,
      });
      setTitle('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="quick-add" onSubmit={submit}>
      <span className="quick-add-plus" aria-hidden>
        +
      </span>
      <input
        className="quick-add-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Add a task…   (try “Make Moonflower Soaps friday”)"
        aria-label="Add a task"
      />

      {parsed.dueDate && (
        <span className="quick-add-parsed">{formatDueLabel(parsed.dueDate, today)}</span>
      )}

      <select
        className="quick-add-select"
        value={due}
        onChange={(e) => setDue(e.target.value)}
        aria-label="Due"
        disabled={Boolean(parsed.dueDate)}
      >
        {DUE_CHOICES.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>

      <select
        className="quick-add-select"
        value={assignee}
        onChange={(e) => setAssignee(e.target.value)}
        aria-label="Assignee"
      >
        <option value="">Unassigned</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.display_name || p.email}
          </option>
        ))}
      </select>
    </form>
  );
}
